import React, { useEffect, useState } from 'react';

import { useToggleableLayerConfigs } from '../components/Maps/hooks/useToggleableLayersConfig';
import { TOGGLEABLE_LAYERS } from '../components/Maps/mapData/toggleableLayers';
import {
  DrawerView,
  LayerConfig,
  LayerName,
  Layers,
  ToggleableGroup,
} from '../components/Maps/types/types';
import { useUserInfo } from '../helpers/hooks/useUserInfo';
import { useExplore } from '../views/Explore/context/ExploreContext';

interface MapLayersContext {
  layers: Layers;
  toggleLayer: (layer: LayerName) => void;
  toggleToggleableGroup: (layer: LayerName, toggleableGroup: ToggleableGroup) => void;
  setToggleableGroups: (layer: LayerName, toggleableGroups: ToggleableGroup[]) => void;
  setNewLayerConfig: (layer: LayerName, layerConfig: Omit<LayerConfig, 'setConfig'>) => void;
}

const DEFAULT_LAYERS: MapLayersContext['layers'] = Object.keys(TOGGLEABLE_LAYERS).reduce(
  (result, layerName) => ({
    ...result,
    [layerName]: {
      isWholeLayerVisible: false,
      toggleableGroups: [],
      setConfig: () => {},
    },
  }),
  {},
) as Layers;

const MapLayersContext = React.createContext<MapLayersContext>({
  layers: DEFAULT_LAYERS,
  toggleLayer: () => {},
  toggleToggleableGroup: () => {},
  setToggleableGroups: () => {},
  setNewLayerConfig: () => {},
});

// non-mutating on purpose; allowing multi-users on one machine
const filterProLayers = (layers: Layers, isPro: boolean, isLoadingUser: boolean): Layers => {
  if (isLoadingUser || isPro) {
    return layers;
  }
  return Object.entries(layers).reduce((filteredLayers, [layerNameString, layer]) => {
    const layerName = layerNameString as LayerName;
    const isWholeLayerVisible =
      layer.isWholeLayerVisible && !TOGGLEABLE_LAYERS[layerName].premiumOnly;
    const { toggleableGroups } = layers[layerName];

    return {
      ...filteredLayers,
      [layerName]: { isWholeLayerVisible, toggleableGroups },
    };
  }, {}) as Layers;
};

const handleVisibilityBasedOnURL = (layers: Layers, drawerView: DrawerView | undefined): void => {
  Object.entries(layers).forEach(([layerName, layerConfig]) => {
    const isVisible =
      TOGGLEABLE_LAYERS[layerName as LayerName].shouldShowBasedOnUrl(drawerView) ||
      layerConfig.isWholeLayerVisible;

    layerConfig.setConfig({
      isWholeLayerVisible: isVisible,
      toggleableGroups: layerConfig.toggleableGroups,
    });
  });
};

export const MapLayersProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const { layers, isLoaded } = useToggleableLayerConfigs();
  const [filteredLayers, setFilteredLayers] = useState(layers);
  const { isPremium, isLoadingUser } = useUserInfo();
  const { exploreQueryParams } = useExplore();

  useEffect(() => {
    setFilteredLayers(filterProLayers(layers, isPremium, isLoadingUser));
  }, [isPremium, isLoadingUser, layers, setFilteredLayers]);

  useEffect(() => {
    if (isLoaded) {
      handleVisibilityBasedOnURL(layers, exploreQueryParams.drawerView);
    }
    // we only want to trigger this once (after the data are loaded from local storage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  const toggleLayer = (layer: LayerName): void => {
    const { setConfig, isWholeLayerVisible, toggleableGroups } = layers[layer];

    setConfig({ isWholeLayerVisible: !isWholeLayerVisible, toggleableGroups });
  };

  const toggleToggleableGroup = (layer: LayerName, toggleableGroup: ToggleableGroup): void => {
    const { setConfig, isWholeLayerVisible, toggleableGroups } = layers[layer];

    const isToggleGroupActive = toggleableGroups.includes(toggleableGroup);
    const newToggleableGroups = isToggleGroupActive
      ? toggleableGroups.filter(group => group !== toggleableGroup)
      : [...toggleableGroups, toggleableGroup];

    setConfig({ isWholeLayerVisible, toggleableGroups: newToggleableGroups });
  };

  const setToggleableGroups = (layer: LayerName, toggleableGroups: ToggleableGroup[]): void => {
    const { setConfig, isWholeLayerVisible } = layers[layer];

    setConfig({ isWholeLayerVisible, toggleableGroups });
  };

  const setNewLayerConfig = (
    layer: LayerName,
    layerConfig: Omit<LayerConfig, 'setConfig'>,
  ): void => {
    const { setConfig } = layers[layer];

    setConfig(layerConfig);
  };

  return (
    <MapLayersContext.Provider
      value={{
        layers: filteredLayers,
        toggleLayer,
        toggleToggleableGroup,
        setToggleableGroups,
        setNewLayerConfig,
      }}
    >
      {children}
    </MapLayersContext.Provider>
  );
};

export const useMapLayers = (): MapLayersContext => {
  const context = React.useContext(MapLayersContext);
  if (context === undefined) {
    throw new Error('useMapLayers must be used within a MapLayersProvider');
  }
  return context;
};
